import { Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AlertifyService } from '../../services/alertify.service';
import { ProjectService } from '../../services/project.service';
import { QuotaService } from '../../services/quota.service';
import { Project, ProjectDedupeStatus, ProjectSurveyPartner } from '../../models/project';

interface ChecklistPartner {
  partnerId: string;
  name: string;
  fullQuota: number;
  softQuota: number;
  cpi: number;
  testIdsReturned: boolean;
  lastTestedOn: string | null;
  missingAllocation: boolean;
  missingSoftQuota: boolean;
  cannotRun: boolean;
  // NK-415: a vendor already past Test when the project soft launches. It gets no checkbox —
  // there is nothing to decide, and re-sending a status would silently demote a Full Launch
  // vendor to Soft Launch, which is the one outcome nobody asked for.
  alreadyLaunched: boolean;
  fieldStatus: string;
  fieldStatusClass: string;
  // NK-415: ticked for everyone else on open. Soft launch target is deliberately NOT used to
  // seed this — it records the size of the slice, not the intent to launch, so inferring from
  // it would hold vendors back on a number nobody sets with that meaning.
  selected: boolean;
}

type ChecklistItem = 'vendors' | 'quotas' | 'dates' | 'specs' | 'quality';

/**
 * One entry per section of the checklist. `question` is the whole point of the section —
 * it's what the user is actually being asked, and it's shown next to the Yes button that
 * confirms it. `summary` is the collapsed one-line state, `flagged` decides whether the
 * section sorts to the top and reads in the alert tone.
 *
 * `noLabel` names the section's destination in the one place a link ever appears: under the
 * question, as "No - <noLabel>". There used to be a second label for a second placement - a
 * fix link tucked inline at the end of the last finding - so the way out moved depending on
 * whether NAV had found anything. One destination reached from two places is one the reader
 * has to hunt for, so the inline placement was dropped: findings now only report what NAV
 * observed, and the answer to the question always sits with the question. Every section has a
 * noLabel, so no section can ever ask a yes/no question and offer only Yes.
 */
interface ChecklistSection {
  key: ChecklistItem;
  title: string;
  flagged: boolean;
  /**
   * Required setup this section needs is absent, so the section cannot be confirmed at all —
   * as opposed to `flagged`, which marks something to weigh and confirm anyway. The rule
   * dividing the two: a blocker is a required value that is *missing*, a flag is a value that
   * is present and worth a second look. That keeps NAV out of judgement calls it can't make —
   * an allocation total above the study goal is often deliberate, so it flags; a vendor with no
   * allocation at all cannot be deliberate, so it blocks.
   */
  blocked: boolean;
  summary: string;
  question: string;
  linkSection: string;
  noLabel: string;
}

/**
 * Project tabs are reached as `/projects/<id>/<slug>`, where the slug is the section's
 * *title* lowercased with spaces hyphenated (project-details.component.ts:364) — and those
 * titles are free text an admin can rename per project type. So the real slug is resolved
 * from the project's own section config at load; these are only the stock titles, used if
 * a project type somehow has no matching section.
 *
 * `settings` is different and safe to hardcode: it's a subsection with a literal slug
 * (project-details.component.ts:136,602), not a renameable section.
 */
const SECTION_FALLBACK_SLUGS: Record<string, string> = {
  'links-and-vendors': 'links-&-vendors',
  'quotas': 'quotas'
};

const SETTINGS_SLUG = 'settings';

const REDIRECT_FIELDS: (keyof ProjectSurveyPartner)[] =
  ['completeRedirectURL', 'terminateRedirectURL', 'overQuotaRedirectURL', 'qcRedirectURL'];

/**
 * NK-415: statuses that mean a vendor is already receiving traffic, so this launch has nothing
 * to do to it. Lower-cased on both sides at the point of use — the status is a free-text column,
 * not an enum, and the casing it comes back in is not guaranteed.
 */
const LAUNCHED_STATUSES = new Set(['soft launch', 'full launch']);

/**
 * NK-415: the status pill classes, matching dropdown-status.component.ts:72 exactly. The map is
 * repeated rather than imported because that component owns a control - a dropdown that changes
 * the status - and this modal reads and confirms, it never edits. What is shared is the look, so
 * only the look is copied; see the .field-status rules for why the styles are repeated too.
 */
const STATUS_CLASSES: Record<string, string> = {
  'test': 'nav-status-test',
  'soft launch': 'nav-status-soft-launch',
  'full launch': 'nav-status-full-launch',
  'paused': 'nav-status-paused',
  'closed': 'nav-status-closed',
  'invoiced': 'nav-status-invoiced'
};

const ALL_ITEMS: ChecklistItem[] = ['vendors', 'quotas', 'dates', 'specs', 'quality'];

// Long enough to swallow the focus/visibilitychange pair a single tab switch fires, short
// enough that a real trip to another page and back is never mistaken for one.
const RETURN_DEBOUNCE_MS = 1000;

const MS_PER_DAY = 86400000;

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const isSet = (v: unknown) => !!(v as string)?.trim();

@Component({
  selector: 'or-go-live-checklist',
  templateUrl: './go-live-checklist.component.html',
  styleUrls: ['./go-live-checklist.component.scss'],
  standalone: false
})
export class GoLiveChecklistComponent implements OnInit {

  @Input() projectId: string;
  @Input() partnerId: string;
  @Input() partnerName: string;
  @Input() status: string;
  @Input() oldStatus: string;

  @Output() saved = new EventEmitter();
  @Output() close = new EventEmitter();

  loading = false;
  refreshing = false;
  saving = false;

  project: Project = null;
  launchDetails: any = null;
  quotaGroupNames: string[] = [];
  partners: ChecklistPartner[] = [];
  dedupeProjectNames: string[] = [];
  // The statuses the duplication rules apply to, read from the project's own row in
  // projectDedupeStatus. Settings' own wording, in its own column order.
  dedupeStatuses: string[] = [];
  showDedupeList = false;
  projectIR: number = null;
  projectLOI: number = null;
  surveyLink: string = null;

  showQuotas = true;
  hasQuotas = false;

  fieldStartDate: Date = null;
  fieldEndDate: Date = null;
  curDate = new Date();

  // One section open at a time. Ticking a section's confirm control closes it and opens
  // whichever section still needs attention next.
  openKey: ChecklistItem | null = null;

  // Derived from the loaded data once per load, not recomputed per change-detection cycle.
  // See buildSections() for why. Empty until the first load resolves, which allConfirmed
  // accounts for so an error path can't leave Confirm & Launch enabled with nothing confirmed.
  sections: ChecklistSection[] = [];
  private notes: Partial<Record<ChecklistItem, string[]>> = {};
  private blockers: Partial<Record<ChecklistItem, string[]>> = {};
  // NK-415: the box's lead line, above the bullets. Cached with the other two for the same
  // reason - the template reads it on every change-detection cycle and the vendors case walks
  // the partner list three times to build it.
  private leads: Partial<Record<ChecklistItem, string>> = {};

  confirmed: Record<ChecklistItem, boolean> = {
    vendors: false,
    quotas: false,
    dates: false,
    specs: false,
    quality: false
  };

  // NK-315: "log every confirmation with user and timestamp" — the moment each box was
  // ticked, so a confirmation of a section that visibly said "Not tested" is attributable.
  // The user comes from the auth token server-side; the frontend supplies the when.
  confirmedAt: Partial<Record<ChecklistItem, string>> = {};

  cleanIdThresholds = { '1000': 'No Threshold', '30': 'Conservative (30)', '25': 'Moderate (25)', '20': 'Aggressive (20)', '-2': 'Vendor Based', '-1': 'Using API' };

  // NK-415: the home that open item needed. The old toggle was all-or-nothing for the whole
  // project; this is per vendor, because "we don't always SL all vendors at once" was the
  // actual request. The launch no longer asks the API to fan out across vendors at all — it
  // moves the project, then moves exactly the vendors that are ticked, so an unticked vendor
  // is not written to rather than being written back to Test.
  private launchFailures: string[] = [];

  // Set the first time the user opens a Nav page to fix something, so returning to this tab
  // re-reads the project instead of showing what was true before they left. Sticky for the
  // life of the modal — see onReturnFromFix.
  private awaitingFix = false;
  private lastRefreshAt = 0;
  private signatures: Partial<Record<ChecklistItem, string>> = {};
  private sectionSlugs: Record<string, string> = {};

  constructor(
    private projectService: ProjectService,
    private quotaService: QuotaService,
    private alertify: AlertifyService
  ) { }

  ngOnInit() {
    this.loading = true;
    this.load();
  }

  /**
   * NK-315: a messy project means leaving to fix something and coming back. Fix links open
   * in a new tab so this modal (and everything already ticked) stays alive, and returning
   * re-reads the project so the user is never confirming a stale screen — the user never has
   * to close and reopen the checklist to see a change they just made.
   *
   * Armed once and then left armed, for the rest of the modal's life. It used to disarm on the
   * first return, which broke the case it exists for as soon as a section had more than one
   * fault: fix the first, come back to check it, switch to the fix tab that's still open, fix
   * the second, come back — and that return did nothing, because the flag had already been
   * spent. The only way to re-arm it was to click the fix link again, from a modal that by then
   * was showing stale data. One click now covers every trip.
   *
   * Two triggers, because neither is reliable alone: window focus doesn't fire when the user
   * returns via a tab switch in some browsers, and visibilitychange doesn't fire when the fix
   * tab was opened in a separate window that merely sat on top of this one. Both fire on an
   * ordinary tab switch, so the duplicate is dropped on elapsed time — which is what disarming
   * the flag used to do, at the cost of only ever working once. The same window also absorbs a
   * fast alt-tab away and back, which can't have been a fix.
   */
  @HostListener('window:focus')
  @HostListener('document:visibilitychange')
  onReturnFromFix() {
    if (document.visibilityState === 'hidden') return;
    if (!this.awaitingFix || this.loading || this.refreshing || this.saving) return;
    if (Date.now() - this.lastRefreshAt < RETURN_DEBOUNCE_MS) return;
    this.lastRefreshAt = Date.now();
    this.refreshing = true;
    this.load();
  }

  private load() {
    // QuotaService caches GetQuota per project with shareReplay(1) and invalidates it on its own
    // writes — but a quota added on the Quotas page happens in a *different tab*, which has its
    // own service instance, so this tab would replay the pre-quota response forever and the
    // "No quotas set up" note would never clear. Dropped on every load, not just refreshes: the
    // cache can already be stale by the time the modal first opens. This screen is a sign-off,
    // so it reads from source every time — it runs a handful of times per launch, not per click.
    this.quotaService.invalidateQuotaCache(this.projectId);

    forkJoin({
      project: this.projectService.GetProject(this.projectId, ''),
      launchDetails: this.projectService.GetProjectForLaunchConfirmation(this.projectId),
      quota: this.quotaService.GetQuota(this.projectId),
      projectTypes: this.projectService.GetProjectTypes(null, true, false),
      // Advisory and never blocks, so a failure degrades to "Not tested" everywhere rather
      // than failing the load over a column that doesn't gate the launch.
      testActivity: this.projectService.GetVendorTestActivity(this.projectId).pipe(catchError(() => of([])))
    }).subscribe(({ project, launchDetails, quota, projectTypes, testActivity }) => {
      this.project = project;
      this.launchDetails = launchDetails;

      // Resolve each tab's real URL slug from this project type's own section titles, so a
      // renamed tab still links somewhere real instead of 404-ing on a guessed slug.
      const projectType = projectTypes?.find(t => t.id === project?.projectTypeId);
      (projectType?.sections ?? []).forEach(s => {
        if (s?.type && s?.title) this.sectionSlugs[s.type] = s.title.toLowerCase().replace(/\s+/g, '-');
      });

      // NK-315: group names only. No per-cell targets — limitN/limitPercent aren't populated
      // by GetQuota as-is (every real consumer derives them client-side via a processQuota()
      // step this modal doesn't run), and per-option labels only exist for the built-in
      // demographic fields. The cell count went too: a bare number with nothing to check it
      // against was one more thing to read past on the way to the question, and the group
      // names already answer whether quotas exist at all. Real targets get verified on the
      // Quotas page, which this section links to.
      // Country/Division excluded — internal bookkeeping groups, not question quotas.
      const groups = (quota?.quotaGroups ?? [])
        .filter(g => !g.isDeleted && g.name !== 'Country' && g.name !== 'Division');
      this.quotaGroupNames = groups.map(g => g.name);
      this.hasQuotas = this.quotaGroupNames.length > 0;
      this.showQuotas = project?.category !== 'Healthcare';

      // Read-back only — there are no date pickers in this modal, so the stored value is
      // always the truth and there's no in-progress edit to preserve.
      //
      // Falls back to the project record. The launch-details DTO is the same source the plain
      // confirmation modal reads (launch-confirmation.component.ts:55), but it renders empty
      // date *pickers* when the DTO omits them, which still looks like a form — here an absent
      // date meant the panel rendered nothing at all, which is what "the dates didn't show"
      // looked like. GetProject is already in this forkJoin and carries both fields, so there's
      // no reason to depend on a single source for them.
      this.fieldStartDate = this.toDate(launchDetails?.fieldStartDate ?? project?.fieldStartDate);
      this.fieldEndDate = this.toDate(launchDetails?.fieldEndDate ?? project?.fieldEndDate);

      const segment = project?.projectSegments?.[0];
      // Incidence and LOI are set on the segment (Settings writes projectSegments[0].projectIR /
      // .projectLOI, see project-settings.component.html:590,606) — not Project.ir /
      // Project.medianLoiDisplay, which are a different, unrelated pair of fields.
      this.projectIR = segment?.projectIR ?? null;
      this.projectLOI = segment?.projectLOI ?? null;
      // The same field Links & Vendors edits, so a link added there and a return to this tab
      // agree. launchDetails carries its own copy; the segment is the one being written.
      this.surveyLink = segment?.surveyLink ?? launchDetails?.surveyLink ?? null;

      // A vendor absent from the response has no test evidence.
      const testedOn = new Map<string, string>(
        (testActivity ?? []).map(t => [t.partnerId?.toLowerCase(), t.lastTestedOn])
      );

      const rawPartners: ProjectSurveyPartner[] = segment?.projectSurveyPartners?.filter(p => !p.isDeleted) ?? [];
      this.partners = rawPartners.map(p => ({
        partnerId: p.partnerId,
        name: p.partnerName,
        fullQuota: p.partnerFullQuota,
        // NK-315: previously backfilled 0 to partnerFullQuota/10, on the assumption that
        // Links & Vendors does the same before displaying it. It doesn't — that formula
        // (project-links-and-vendors.ts:591 autoUpdateSoftLaunch) only runs on (change) of
        // a brand-new vendor's full-quota input while adding one; it's never applied to an
        // already-saved vendor's display anywhere else in the app (see
        // project-card-field-plan.component.html:206, which shows the raw stored value).
        // Showing a fabricated number here just because it's 0 was the actual bug.
        softQuota: p.partnerSoftQuota,
        cpi: p.buyCPI,
        testIdsReturned: testedOn.has(p.partnerId?.toLowerCase()),
        lastTestedOn: testedOn.get(p.partnerId?.toLowerCase()) ?? null,
        missingAllocation: !(p.partnerFullQuota > 0),
        // A vendor can be set up and still have a soft launch target of 0 — guaranteed zero
        // traffic in soft launch, which is worth flagging the same as no allocation at all.
        missingSoftQuota: p.partnerFullQuota > 0 && !(p.partnerSoftQuota > 0),
        // Which redirects a vendor needs varies (some take only completes, some handle
        // terminations their side, QC is often unused), so a partial set is never remarked
        // on — only a vendor that cannot function at all.
        cannotRun: !REDIRECT_FIELDS.some(f => isSet(p[f])),
        alreadyLaunched: LAUNCHED_STATUSES.has((p.partnerStatus ?? '').trim().toLowerCase()),
        fieldStatus: p.partnerStatus,
        fieldStatusClass: STATUS_CLASSES[(p.partnerStatus ?? '').trim().toLowerCase()] ?? '',
        selected: !LAUNCHED_STATUSES.has((p.partnerStatus ?? '').trim().toLowerCase())
      }));

      const dedupeProjects: ProjectDedupeStatus[] = project?.projectDedupeStatus ?? [];
      // The project's own row carries the statuses the duplication rules apply to — it is not
      // another project this one is de-duped against. Identified the way the backend identifies
      // it (`OtherId == project.Id`, PublicAPIService.cs:167 and :505), not by position: it only
      // sorts first by convention, and it comes back with `otherName` set to this project's own
      // name (ProjectService.cs:495), so mapping every row listed the project against itself and
      // counted it in the total.
      const ownRow = dedupeProjects.find(d => d.otherId === project?.id);
      this.dedupeProjectNames = dedupeProjects.filter(d => d !== ownRow).map(d => d.otherName).filter(Boolean);
      this.dedupeStatuses = this.readDedupeStatuses(ownRow);

      // Before applySignatures/restoreOpenSection — both read this.sections.
      this.rebuildDerived();
      this.applySignatures();
      this.restoreOpenSection();
      this.loading = false;
      this.refreshing = false;
    }, () => {
      this.alertify.error('There was an issue loading the go live checklist');
      this.loading = false;
      this.refreshing = false;
    });
  }

  private toDate(value: string | Date): Date {
    return value ? new Date(value) : null;
  }

  /**
   * NK-315: on a refresh, ticks are kept — a user who left to add a vendor should not have to
   * re-confirm the five sections they never touched. But a tick is an attestation to specific
   * values, so any section whose values actually changed while they were away drops its tick
   * and has to be looked at again.
   */
  private applySignatures() {
    const next: Record<ChecklistItem, string> = {
      vendors: this.partners.map(p => `${p.name}|${p.fullQuota}|${p.softQuota}|${p.cpi}|${p.testIdsReturned}`).join(';') + `|${this.completesValue}|${this.hasSurveyLink}`,
      quotas: this.quotaGroupNames.join(','),
      dates: `${this.fieldStartDate?.getTime() ?? ''}|${this.fieldEndDate?.getTime() ?? ''}`,
      specs: `${this.completesValue}|${this.projectIR}|${this.projectLOI}`,
      quality: [
        this.project?.isDeDupeDeviceEnabled,
        this.project?.isDupeTrackingOnly,
        this.dedupeStatuses.join(','),
        this.dedupeProjectNames.join(','),
        this.project?.isFraudDetectionEnabled,
        this.project?.fraudDetectionThreshold,
        this.project?.allowMobile,
        this.linkSecurityOn
      ].join('|')
    };

    (Object.keys(next) as ChecklistItem[]).forEach(key => {
      if (this.signatures[key] !== undefined && this.signatures[key] !== next[key]) {
        this.confirmed[key] = false;
        delete this.confirmedAt[key];
      }
    });
    this.signatures = next;
  }

  /**
   * Recomputes everything derived from the loaded project: the section list and each section's
   * notes. Called once per load() — including the refresh after a user returns from fixing
   * something — and nowhere else, because none of it depends on confirmation state. Confirming a
   * section changes `confirmed`, which only `remainingCount` reads, and that's a filter over six
   * items.
   */
  private rebuildDerived() {
    this.notes = {};
    this.blockers = {};
    this.leads = {};
    ALL_ITEMS.forEach(key => {
      this.notes[key] = this.computeSectionNotes(key);
      this.blockers[key] = this.computeSectionBlockers(key);
      this.leads[key] = this.computeSectionLead(key);
    });
    // After the caches, not before: each section's `blocked` is read from them.
    this.sections = this.buildSections();
  }

  /* ---------- open / confirm ---------- */

  toggleOpen(key: ChecklistItem) {
    this.openKey = this.openKey === key ? null : key;
    if (this.openKey) this.revealOpenSection();
  }

  /**
   * The only way to confirm a section the first time. The bar's checkbox is disabled while
   * unconfirmed — a direct product ask to stop sections being ticked off from the collapsed
   * row without the panel ever being opened and read — then unlocks once confirmed, so a
   * mistaken Yes can be undone directly from the bar without reopening the panel.
   */
  confirmSection(key: ChecklistItem) {
    if (this.isBlocked(key)) return;
    this.confirmed[key] = true;
    this.confirmedAt[key] = new Date().toISOString();
    this.openNextConfirmable();
  }

  /** Undo: only reachable once confirmed, since that's the only state the checkbox is enabled in. */
  unconfirmSection(key: ChecklistItem) {
    this.confirmed[key] = false;
    delete this.confirmedAt[key];
  }

  private openFirstUnconfirmed() {
    this.openSection(this.sections.find(s => !this.confirmed[s.key]));
  }

  /**
   * The auto-advance after a tick, which — unlike the initial open — steps *over* blocked
   * sections. Their Yes is disabled, so advancing into one strands the user on a panel they
   * can't act on, and it would do it again after every subsequent tick, since blocked sections
   * sort to the top and never clear. They come last instead, once there's nothing left to
   * confirm, which is the right moment to land on the thing that has to be fixed elsewhere.
   */
  private openNextConfirmable() {
    const unconfirmed = this.sections.filter(s => !this.confirmed[s.key]);
    this.openSection(unconfirmed.find(s => !s.blocked) ?? unconfirmed[0]);
  }

  private openSection(section: ChecklistSection | undefined) {
    this.openKey = section ? section.key : null;
    if (this.openKey) this.revealOpenSection();
  }

  /**
   * NK-315: on a refresh, the section the user left from stays open.
   *
   * This used to call openFirstUnconfirmed() unconditionally, which broke the fix-and-return
   * flow in exactly the way that flow exists to avoid. Someone on Quotas clicks "Set up quotas",
   * sets them up, comes back — and lands on Vendors, because that's the first unconfirmed
   * section in the list. The section they just fixed has meanwhile sorted *downwards* (it no
   * longer flags, and flagged sections sort first), so the one thing they wanted to see the
   * result of is now both closed and somewhere else. They have to go find it and reopen it,
   * which is the thing the new-tab behaviour was built to spare them.
   *
   * Staying put also makes the refresh legible: they return to the same panel and watch the
   * note disappear, the summary change and the amber dot clear. That *is* the "it updated"
   * signal — no separate banner needed.
   *
   * Falls back to the first unconfirmed section when there's nothing to hold on to: the very
   * first load, or a section that has since been confirmed or has disappeared entirely
   * (Quotas does, if the project's category changed while they were away).
   */
  private restoreOpenSection() {
    const held = this.openKey
      && this.sections.some(s => s.key === this.openKey)
      && !this.confirmed[this.openKey];

    if (held) this.revealOpenSection();
    else this.openFirstUnconfirmed();
  }

  /**
   * The list is capped at a max-height and scrolls past that, so the section that
   * auto-advances after a tick can still sit below the fold — which reads as nothing having
   * happened. Waits a tick for the panel to render before measuring.
   */
  private revealOpenSection() {
    const key = this.openKey;
    setTimeout(() => {
      if (this.openKey !== key) return;
      document.getElementById(`glc-section-${key}`)?.scrollIntoView({ block: 'nearest' });
    });
  }

  /**
   * NK-315 feedback: opens in a new tab rather than navigating this one. Same-tab navigation
   * would destroy the modal, and on a project with several problems that means reopening the
   * checklist and re-ticking everything once per fix.
   */
  openFixPage(sectionType: string) {
    const slug = sectionType === SETTINGS_SLUG
      ? SETTINGS_SLUG
      : (this.sectionSlugs[sectionType] ?? SECTION_FALLBACK_SLUGS[sectionType]);
    if (!slug) return;
    this.awaitingFix = true;
    window.open(`/projects/${this.projectId}/${slug}`, '_blank');
  }

  isSameDate(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }

  /* ---------- per-section flags and collapsed summaries ----------
   * A flag still doesn't disable anything — it's information the user weighs, and `flagged`
   * decides only whether the section sorts up and whether its collapsed summary reads in the
   * alert tone. What gates a section is `blocked`, which is computed separately from missing
   * setup rather than from these. Every blocking condition does also flag, so a blocked
   * section is always marked in its collapsed bar; the reverse doesn't hold. */

  get vendorsReadyCount(): number {
    return this.partners.filter(p => !p.missingAllocation && !p.missingSoftQuota).length;
  }

  /**
   * Deliberately does not feed `vendorsFlagged`: testing status never gates this launch, so it
   * never earns the amber dot. It's here to be read, not to raise an alarm.
   */
  get vendorsTestedCount(): number {
    return this.partners.filter(p => p.testIdsReturned).length;
  }

  // The allocation total is a division of the study's contracted completes goal, so a sum
  // above the goal is as much a setup error as a sum below it — see allocationLabel.
  get allocationMismatch(): boolean {
    return this.completesValue > 0 && this.totalVendorAllocation !== this.completesValue;
  }

  /**
   * NK-315: "no valid live URL" is one of the three required-config checks in the AC, and the
   * standalone Live URL step that used to carry it was dropped on 2026-08-18. It lives in
   * Vendors because that's the section whose fix link already goes to Links & Vendors, where
   * the field is (labelled "Live URL" there, which is what this section's copy calls it too).
   */
  get hasSurveyLink(): boolean {
    return isSet(this.surveyLink);
  }

  get vendorsFlagged(): boolean {
    return this.partners.length === 0 || !this.hasSurveyLink
      || this.vendorsReadyCount < this.partners.length || this.allocationMismatch;
  }

  /**
   * Always the tested count once there are vendors — this row's standing heads-up. It used to
   * switch to a ready count or "No live URL" when something was wrong, which meant the one
   * number the user wanted at a glance disappeared exactly when the row got interesting. Faults
   * are signalled by the amber dot and detailed in the box; they don't need to displace this.
   */
  get vendorsSummary(): string {
    if (this.partners.length === 0) return 'None set up';
    // Nothing else earns a place here. An allocation mismatch deliberately gets no summary of
    // its own: the bar is a scan line, and "Over the study goal" put a verdict in it. A bare
    // vendor count ("3 vendors") said nothing the panel didn't, and the allocation total
    // ("3 vendors · 125 allocated") was a figure to be checked rather than a reason to look —
    // that belongs in the table, next to the goal it's measured against.
    return `${this.vendorsTestedCount} of ${this.partners.length} tested`;
  }

  get quotasFlagged(): boolean {
    return !this.hasQuotas;
  }

  get quotasSummary(): string {
    return this.hasQuotas ? this.quotaGroupNames.join(', ') : 'None set up';
  }

  // A field period that has already ended can't be soft launched into — that's a different
  // class of problem from a start date that simply isn't today, which stays advisory only.
  get datesInPast(): boolean {
    if (!this.fieldEndDate) return false;
    const endOfDay = new Date(this.fieldEndDate);
    endOfDay.setHours(23, 59, 59, 999);
    return endOfDay.getTime() < this.curDate.getTime();
  }

  get datesFlagged(): boolean {
    return !this.fieldStartDate || !this.fieldEndDate || this.datesInPast;
  }

  get startsToday(): boolean {
    return !!this.fieldStartDate && this.isSameDate(this.fieldStartDate, this.curDate);
  }

  get startDateWarning(): boolean {
    return !this.datesFlagged && !this.startsToday;
  }

  get daysInField(): number {
    if (!this.fieldStartDate || !this.fieldEndDate) return 0;
    return Math.max(0, Math.round((this.fieldEndDate.getTime() - this.fieldStartDate.getTime()) / MS_PER_DAY));
  }

  get completesValue(): number {
    return this.launchDetails?.fullLaunchAllocation || this.project?.expectedCompletes || 0;
  }

  /**
   * NK-315 feedback: with the new bidding tool, the vendor set can eventually be checked
   * against a field plan directly — not built yet, so this is the stand-in until it lands.
   *
   * Sums partnerFullQuota, NOT partnerSoftQuota. The question is "does the vendor plan cover
   * the study we sold" — a full-launch question. Summing the soft launch targets (what the
   * table shows) and comparing that to the project's completes goal would compare a
   * deliberately small first release against the whole study and read as massively
   * under-allocated on every correctly configured project.
   */
  get totalVendorAllocation(): number {
    return this.inPlayPartners.reduce((sum, p) => sum + (p.fullQuota || 0), 0);
  }

  /**
   * The soft launch slice, totalled. Shown in the table's own totals row rather than compared
   * to anything: there's no soft launch goal in this modal to reconcile it against, and the
   * ratio of a vendor's SL target to its full allocation is a judgement NAV has no business
   * forming — showing the pair is what lets the user make it.
   */
  /**
   * NK-415. The vendors this launch can act on — everything not already past Test. Drives the
   * scope line's denominator, so a project whose vendors are all already launched says so
   * instead of claiming "all 0 vendors".
   */
  get selectablePartners(): ChecklistPartner[] {
    return this.partners.filter(p => !p.alreadyLaunched);
  }

  /** The vendors this launch will actually write a status to. */
  get selectedPartners(): ChecklistPartner[] {
    return this.selectablePartners.filter(p => p.selected);
  }

  /**
   * NK-415: everything that will be in soft launch once this launch is done — ticked vendors
   * plus the ones already there. One rule, used by the totals, the notes and the blockers, so
   * the three cannot disagree about which vendors the section is talking about.
   */
  get inPlayPartners(): ChecklistPartner[] {
    return this.partners.filter(p => p.selected || p.alreadyLaunched);
  }

  get heldPartners(): ChecklistPartner[] {
    return this.selectablePartners.filter(p => !p.selected);
  }

  get allSelected(): boolean {
    return this.selectablePartners.length > 0 && !this.heldPartners.length;
  }

  /** Drives the header checkbox's dash: some ticked, but not all. */
  get someSelected(): boolean {
    return !this.allSelected && this.selectedPartners.length > 0;
  }

  /**
   * The totals row label. Counts every vendor, because a vendor already in soft launch is one
   * more vendor that will be live when this is done — the row underneath it counts the same way.
   *
   * Phrased as a state rather than an action. "Soft launching N of M" claimed the launch was
   * moving N vendors, which is only true when none of them were already there; with one already
   * in soft launch and nothing ticked it read "Soft launching 1 of 3" directly above a note
   * saying the project moves on its own and no vendor is going. The figures were right and the
   * verb was wrong. This row says where things end up, the note says what this launch does, and
   * the two no longer answer the same question differently.
   */
  get vendorScopeLabel(): string {
    return `${this.inPlayPartners.length} of ${this.partners.length} in soft launch`;
  }

  /**
   * NK-415 stakeholder feedback: the launch scope, said in words. The note box used to name only
   * the vendors being held, so the launching side existed on screen as a figure in the totals row
   * and nowhere as a sentence - a note stating that the selected vendors move into Soft Launch
   * was what came back as the one thing that would make this clearer.
   *
   * Both halves in one line rather than two, because they are one decision: who is going and who
   * is not. Split across two bullets they read as two separate findings about the same table.
   *
   * Always returned once there is a vendor this launch could act on, including when nothing is
   * held. It is the frame the rest of the box is read against - "Prodege has a soft launch target
   * of 0" only means something once it is known that Prodege is in the launch - so it cannot be
   * the line that appears only when something went a particular way. Rendered as the box's first
   * bullet; see sectionLead.
   *
   * Empty when every vendor is already past Test: there is nothing for this launch to decide, and
   * the Status column beside each row already says where they are.
   */
  get vendorScopeNote(): string {
    const selectable = this.selectablePartners;
    if (!selectable.length) return '';

    const going = this.selectedPartners;
    const held = this.heldPartners;

    // The whole table held. Worth its own sentence rather than an empty first half: the project
    // still moves on its own, which is a legitimate thing to do - soft launching to exercise the
    // live links without putting a vendor into field - and nothing else on screen says so.
    //
    // The held vendors are named rather than generalised. "Every vendor stays in test" was the
    // first version and it is false the moment one of them is already launched: that vendor is
    // not in the held list, is not staying in test, and is sitting two rows above wearing a Soft
    // Launch pill that says so.
    if (!going.length) {
      const one = held.length === 1;
      return `The project moves to Soft Launch on its own. ${this.joinPhrases(held.map(p => p.name))} ${one ? 'stays' : 'stay'} in test. Launch ${one ? 'it' : 'them'} from the field plan when you are ready.`;
    }

    const goingLine = `${this.joinPhrases(going.map(p => p.name))} ${going.length === 1 ? 'moves' : 'move'} to Soft Launch.`;
    if (!held.length) return goingLine;

    // No "allocation, CPI and test links untouched". It reassured against a worry nobody has -
    // unticking a box does not make anyone fear their CPI moved - and it pushed the one useful
    // sentence onto a second line. What is worth saying is where to launch them later, because
    // this checklist only ever runs once and will not ask again. The field plan is the right
    // destination, not Links & Vendors: Links & Vendors is where a vendor's allocation and
    // redirects are set up, the field plan card is where its status is changed.
    const one = held.length === 1;
    return `${goingLine} ${this.joinPhrases(held.map(p => p.name))} ${one ? 'stays' : 'stay'} in test. Launch ${one ? 'it' : 'them'} from the field plan when you are ready.`;
  }

  toggleVendorSelected(partner: ChecklistPartner) {
    if (partner.alreadyLaunched) return;
    partner.selected = !partner.selected;
    this.onSelectionChanged();
  }

  toggleAllSelected() {
    const next = !this.allSelected;
    this.selectablePartners.forEach(p => p.selected = next);
    this.onSelectionChanged();
  }

  /**
   * A tick changes which vendors the section's notes and blockers are about, and both are cached
   * per load rather than recomputed per change-detection cycle, so the cache has to be rebuilt.
   *
   * Re-blocking a section that was already confirmed takes the tick back off it. The alternative
   * is a section reading as signed off while showing a red line underneath, and the footer
   * counting it as done — the Yes has to be given again once the blocker is gone.
   */
  private onSelectionChanged() {
    this.rebuildDerived();
    if (this.confirmed['vendors'] && this.isBlocked('vendors')) {
      this.unconfirmSection('vendors');
    }
  }

  get totalSoftLaunchTarget(): number {
    return this.inPlayPartners.reduce((sum, p) => sum + (p.softQuota || 0), 0);
  }

  /**
   * NK-315 feedback: both directions of mismatch are named, not just the shortfall.
   * partnerFullQuota is each vendor's contracted share of the study's completes, so the
   * allocations are a division of the goal — a sum above it is as much a setup error as a
   * sum below it. Only rendered when allocationMismatch is true, so the exact-match case
   * never needs a sentence here.
   *
   * Direction first, then the goal as a parenthetical gloss. The allocated total is not repeated
   * — it's the last line of the table directly above — so between the two, both sides of the
   * comparison are readable without the sentence having to carry either in full. That's the fix
   * for the original version ("2,450 completes are allocated, but the study only needs 100"),
   * which did the arithmetic in prose while *neither* figure appeared anywhere on screen.
   *
   * The goal lives here rather than as its own row above the table: it's only worth stating
   * when it's being missed, and a permanent "Study goal" row both duplicated Specs and sat at
   * the opposite end of the table from the total it exists to be compared against.
   */
  get allocationLabel(): string {
    const goal = `${this.formatNumber(this.completesValue)} completes`;
    return this.totalVendorAllocation < this.completesValue
      ? `The vendors are allocated less than the study needs (${goal}).`
      : `The vendors are allocated more than the study needs (${goal}).`;
  }

  // Matches the exact "configured" condition behind the "Link Security On/Off" badge on
  // Links & Vendors (project-links-and-vendors.ts:940) — that page treats an empty string
  // the same as null, not as configured, so a plain `!= null` here would disagree with it.
  get linkSecurityOn(): boolean {
    return this.project?.surveySecurityKey != null && this.project.surveySecurityKey !== '';
  }

  // "Start means dedupe on all statuses" — the backend sets the other four true whenever Start
  // is on (PublicAPIService.cs:510-517), so listing them beside it would state one setting five
  // times. Order and wording follow the Settings > Quality grid, since that is the page the
  // user goes to if this is wrong.
  private readDedupeStatuses(row: ProjectDedupeStatus): string[] {
    if (!row) return [];
    if (row.start) return ['Survey start (all statuses)'];

    const statuses: string[] = [];
    if (row.complete) statuses.push('Complete');
    if (row.terminate) statuses.push('Termination');
    if (row.oq) statuses.push('Overquota');
    if (row.qc) statuses.push('Quality Control');
    return statuses;
  }

  // On or off explicitly, not just the mode. `isDeDupeDeviceEnabled` is the master switch on
  // Settings > Quality ("De-Duplication is currently disabled"), and reading `isDupeTrackingOnly`
  // alone reported a switched-off de-duplication as "De-Duplication" — the one state where the
  // read-back most needs to be right.
  get duplicationLabel(): string {
    if (!this.project?.isDeDupeDeviceEnabled) return 'Off';

    return this.project.isDupeTrackingOnly
      ? 'On — tracking only, does not block'
      : 'On — blocks duplicates';
  }

  // CleanID is off two ways: the legacy master toggle, and a threshold of No Threshold, which is
  // what Settings itself tells you to set to turn it off ("No blocking based on CleanID score").
  // Both are named, so a project that scores but never blocks doesn't read as protected.
  get cleanIdLabel(): string {
    if (this.project?.isFraudDetectionEnabled === false) return 'Off';

    const threshold = this.project?.fraudDetectionThreshold;
    if (threshold == null) return 'On — no threshold set';
    if (Number(threshold) === 1000) return 'On — No Threshold, scores but does not block';

    // A value outside the dropdown's own list has no name to print, and "On — undefined" in a
    // record the user is signing off is worse than the raw number.
    const label: string = this.cleanIdThresholds[threshold];
    return label ? `On — ${label}` : `On — threshold ${threshold}`;
  }

  // NK-315 feedback: Completes 0 / Incidence 0% / LOI 00:00 were all confirmable as-is —
  // launching on those blanks goes live and silently fails to collect.
  get missingSpecs(): string[] {
    const missing: string[] = [];
    if (!(this.completesValue > 0)) missing.push('Completes');
    if (!(this.projectIR > 0)) missing.push('Incidence');
    if (!(this.projectLOI > 0)) missing.push('LOI');
    return missing;
  }

  get specsFlagged(): boolean {
    return this.missingSpecs.length > 0;
  }

  // Names the fields that are actually missing rather than restating all three.
  get missingSpecsLabel(): string {
    return this.joinPhrases(this.missingSpecs);
  }

  // Completes only. It used to print all three ("100 · 80% · 2 min") — three unlabelled numbers
  // in a row can't be read at a glance, and the panel is where they get checked anyway. See the
  // note on `sections` for why a summary never previews a panel's contents.
  get specsSummary(): string {
    if (this.specsFlagged) return `${this.missingSpecsLabel} not set`;
    return `${this.formatNumber(this.completesValue)} completes`;
  }

  /* ---------- vendor notes ---------- */

  get advisoryVendorNoteText(): string {
    const cannotRun = this.inPlayPartners
      .filter(p => !p.missingAllocation && !p.missingSoftQuota && p.cannotRun)
      .map(p => p.name);
    if (!cannotRun.length) return '';
    return `${this.joinPhrases(cannotRun)} ${cannotRun.length === 1 ? 'has' : 'have'} no redirects set up.`;
  }

  /**
   * Everything a section has to say about its own state, in one place, so the template just
   * renders whatever comes back rather than repeating the same conditions per section.
   *
   * Cached per load by rebuildDerived(); `sectionNotes` is a lookup, not a computation. The
   * template asks for a section's notes twice per change-detection cycle (the @if guard and the
   * @for), and the vendors case runs several filters and joins each time.
   *
   * One fact per string, and each one short: a plain statement of what's wrong, then what it
   * costs, in two sentences at most. The template renders each on its own line rather than
   * joining them — two facts merged into one paragraph ("… have a soft launch target of 0.
   * 2,450 completes are allocated, but the study needs 100.") read as a run-on that said
   * neither thing clearly. None of these carry a link; the fix link is appended once, after
   * the last line.
   */
  /**
   * NK-415: the line that leads the findings box. Only Vendors has one - it states what the
   * launch is about to do, which is a thing only that section decides.
   *
   * Held apart from the notes because it is ordered and conditioned differently: always first,
   * and present whether or not NAV noticed anything. It renders as an ordinary bullet - set apart
   * visually, undotted and divided off, the box read as two stacked panels and whatever single
   * note followed it was left looking orphaned.
   */
  sectionLead(key: ChecklistItem): string {
    return this.leads[key] ?? '';
  }

  private computeSectionLead(key: ChecklistItem): string {
    return key === 'vendors' ? this.vendorScopeNote : '';
  }

  sectionNotes(key: ChecklistItem): string[] {
    return this.notes[key] ?? [];
  }

  private computeSectionNotes(key: ChecklistItem): string[] {
    switch (key) {
      case 'vendors': {
        // Everything here is a judgement NAV can't make for the user: a vendor deliberately
        // held out of the soft launch, an allocation total that doesn't equal the goal (often
        // deliberate), a vendor with no redirects (some handle them their own side). A missing
        // allocation is the one vendor fault that can't be deliberate — that's in the blockers.
        if (!this.partners.length) return [];
        const notes: string[] = [];
        // NK-415: every line below reads inPlayPartners, not partners. A vendor the user has
        // unticked is not going anywhere, so reporting its soft launch target of 0 or its
        // missing redirects would be NAV raising faults about a decision the user just made.
        // A soft launch target of 0 is a flag, not an error: holding a vendor out of the soft
        // launch is a legitimate choice, and Nav has no way to tell that apart from an
        // oversight. No allocation *at all* stays an error — that one can't be deliberate.
        const noSoftQuota = this.inPlayPartners.filter(p => !p.missingAllocation && p.missingSoftQuota).map(p => p.name);
        if (noSoftQuota.length) {
          notes.push(`${this.joinPhrases(noSoftQuota)} ${noSoftQuota.length === 1 ? 'has' : 'have'} a soft launch target of 0.`);
        }
        if (this.allocationMismatch) notes.push(this.allocationLabel);
        if (this.advisoryVendorNoteText) notes.push(this.advisoryVendorNoteText);
        // The held vendors used to be reported here, as the last bullet in this list. They are
        // now half of the scope line that leads the box - see vendorScopeNote and sectionLead.
        return notes;
      }

      // Informs, it doesn't block: some studies are deliberately open, with no quotas by
      // design, and NAV can't tell that apart from an oversight. Same reasoning as a vendor
      // held out of the soft launch — NAV sees the absence, never the intent.
      case 'quotas':
        return this.hasQuotas ? [] : ["No quotas set up. Cell pace and quota flags can't run without them."];

      case 'dates':
        if (!this.fieldStartDate || !this.fieldEndDate) return [];
        if (this.datesInPast) {
          return [`Field closed on ${this.formatShortDate(this.fieldEndDate)}.`];
        }
        if (this.startDateWarning) {
          // A plain statement, not a question. This used to close with "Should this start today
          // instead?" — the ask existed because, unlike every other note here, this one isn't
          // reporting a fault (a future start date is frequently correct), and without it the
          // line read as an accusation the user couldn't answer.
          //
          // The reframe removes the ask for a different reason than brevity: by the time a
          // full-service project reaches this screen the field window is contracted and moving
          // it isn't realistic, so inviting the user to change the start date proposes something
          // they can't do. So it states the discrepancy and stops: the start date isn't today,
          // which is the whole of what NAV noticed. It deliberately names no downstream
          // consequence - the DIFOT clause that used to close this line was removed because
          // DIFOT is not measured from the field start date, so a true fact was being explained
          // with an untrue one. Not flagged either way — see datesFlagged.
          return [`Field starts ${this.formatShortDate(this.fieldStartDate)}, not today.`];
        }
        return [];

      default:
        return [];
    }
  }

  /**
   * NK-315 AC3: the sections' hard stops. Each string is a required piece of setup that is
   * absent — not a value to weigh, so there is no "confirm it anyway". A section with any of
   * these can't be confirmed (its Yes is disabled, and confirmSection refuses), which leaves
   * remainingCount above zero and so keeps Confirm & Launch disabled too. That is the whole
   * launch block: one rule, enforced in one place, rather than a second gate at the footer.
   *
   * The two sections missing from this switch are deliberate. Quality settings has nothing NAV
   * can detect — de-duplication and CleanID being configured says nothing about whether they
   * suit this study. And "field has already ended" stays a note rather than a blocker: the
   * dates are present, and on a full-service project the window is contracted long before this
   * screen, so blocking on it would strand a launch on a fact nobody here can change.
   */
  private computeSectionBlockers(key: ChecklistItem): string[] {
    switch (key) {
      case 'vendors': {
        const blockers: string[] = [];
        // First, because it makes every other vendor fault moot: with no live URL there is
        // nowhere for any of them to send anyone.
        // No consequence clause. Where another error names a non-obvious downstream effect
        // (pace, quota flags), "nothing can run without a link" explains itself,
        // so the sentence was length for its own sake — and it left no room for the fix link.
        if (!this.hasSurveyLink) blockers.push('No live URL is set.');
        if (!this.partners.length) {
          blockers.push('No vendors set up. Nothing will go to field.');
          return blockers;
        }
        // Grouped by shared reason rather than one line per vendor — with more than one vendor
        // hitting the same problem, a line each read as a wall of text. Testing status is never
        // mentioned: it only ever appears in its own column, and it never blocks.
        // NK-415: inPlayPartners, so unticking a vendor takes its missing allocation off the
        // gate with it. Before this, one unallocated vendor stopped the whole launch — which was
        // right when every vendor was going, and wrong the moment one can be held back. Vendors
        // added for a later wave are exactly the ones likely to have no allocation yet.
        const noAllocation = this.inPlayPartners.filter(p => p.missingAllocation).map(p => p.name);
        if (noAllocation.length) {
          blockers.push(`${this.joinPhrases(noAllocation)} ${noAllocation.length === 1 ? 'has' : 'have'} no allocation set.`);
        }
        return blockers;
      }

      case 'dates':
        return this.fieldStartDate && this.fieldEndDate
          ? []
          : ['No field dates set.'];

      case 'specs':
        return this.specsFlagged
          ? [`${this.missingSpecsLabel} ${this.missingSpecs.length === 1 ? 'is' : 'are'} not set. Pace, DIFOT and the click calculator need them.`]
          : [];

      default:
        return [];
    }
  }

  /** Lookups, not computations — both caches are filled once per load by rebuildDerived(). */
  sectionBlockers(key: ChecklistItem): string[] {
    return this.blockers[key] ?? [];
  }

  isBlocked(key: ChecklistItem): boolean {
    return this.sectionBlockers(key).length > 0;
  }

  /**
   * Whether the section has anything for NAV to report, and so whether the tinted findings box
   * renders at all. It no longer decides where the section's link lives - that is anchored under
   * the question on every section either way. See ChecklistSection.
   */
  hasFindings(key: ChecklistItem): boolean {
    // NK-415: the lead counts. On a project with every vendor ticked and nothing else to report,
    // the scope line is the only thing in the box - and it is the line the feedback asked for, so
    // it has to be able to open the box on its own.
    return !!this.sectionLead(key) || this.sectionNotes(key).length > 0 || this.sectionBlockers(key).length > 0;
  }

  private formatShortDate(d: Date): string {
    return `${MONTH_ABBR[d.getMonth()]} ${d.getDate()}`;
  }

  private joinPhrases(parts: string[]): string {
    if (parts.length < 2) return parts[0] ?? '';
    return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  }

  private formatNumber(n: number): string {
    return (n ?? 0).toLocaleString('en-US');
  }

  /* ---------- sections ---------- */

  /**
   * A section's `summary` answers one question — "do I need to look at this?" — and nothing
   * else. It deliberately does not preview the panel's contents: confirming a section requires
   * opening it (Yes lives in the panel), so a summary can never save the user a click, which
   * makes any value printed here work the reader has to do twice. So it's a flag reason when
   * flagged, the single most decision-relevant fact when clean, and empty when there's neither.
   *
   * Vendors is always first; the rest sort errors, then flagged, then clean, so among those four
   * the first thing the eye lands on is the thing that needs a look. Within each group the
   * destination-grouped reading order holds (dates, specs and quality all live on Settings), so
   * fixing two things on one page stays one trip. Array.prototype.sort is stable, so ties keep
   * this order.
   *
   * Built once per load, not on demand. This was a getter, and the template reads it six times
   * per change-detection cycle — the @for, remainingCount, allConfirmed twice, the all-confirmed
   * @if, and lede — so every cycle rebuilt six objects, ran roughly fifteen dependent getters,
   * made several filter/reduce passes over the partner list, sorted, and formatted numbers, six
   * times over. Angular runs change detection on every event in the zone, so that was constant,
   * not just at load. Nothing here depends on `confirmed`, only on loaded data, so one build per
   * load is sufficient — see rebuildDerived().
   */
  private buildSections(): ChecklistSection[] {
    // No Live URL section. It was the first row through 2026-08-18 and was dropped on product
    // feedback: the link has already been exercised by the time a project reaches this screen,
    // and cutting the step is one fewer thing between the user and going live. The `#or1#` and
    // empty-link checks went with it — deliberately, not by omission.
    const all: ChecklistSection[] = [
      {
        key: 'vendors', title: 'Vendors',
        flagged: this.vendorsFlagged, blocked: this.isBlocked('vendors'), summary: this.vendorsSummary,
        question: 'Is this the full list of vendors, set up correctly?',
        linkSection: 'links-and-vendors',
        noLabel: 'Go to Links & Vendors'
      }
    ];
    if (this.showQuotas) {
      all.push({
        key: 'quotas', title: 'Quotas',
        flagged: this.quotasFlagged, blocked: this.isBlocked('quotas'), summary: this.quotasSummary,
        question: 'Do these match what the client asked for?',
        // "Set up quotas" only ever follows "No quotas set up"; the no-path is reached with
        // quotas already configured, so it can't tell the user to set them up.
        linkSection: 'quotas', noLabel: 'Go to Quotas'
      });
    }
    all.push(
      {
        key: 'dates', title: 'Field dates',
        flagged: this.datesFlagged, blocked: this.isBlocked('dates'), summary: this.datesSummary,
        // Not "Are these dates right?" any more. On a full-service project the field window is
        // contracted well before this screen, so asking whether the dates are right invites a
        // change that can't realistically be made at the point of going live. What is still
        // live at this moment is the commitment: the end date is the deadline the study is
        // being delivered to, so the act here is acknowledging it rather than reviewing the
        // schedule.
        question: "Is this the deadline we're delivering to?",
        linkSection: 'settings',
        noLabel: 'Go to Project Settings'
      },
      {
        key: 'specs', title: 'Specs',
        flagged: this.specsFlagged, blocked: this.isBlocked('specs'), summary: this.specsSummary,
        question: 'Do these match what we sold?',
        // NAV can see these three are populated, never whether they match the contract, so
        // this section's findings are always empty and the no-path is its only link.
        linkSection: 'settings',
        noLabel: 'Go to Project Settings'
      },
      {
        key: 'quality', title: 'Quality settings',
        // No summary, deliberately. This section never flags, so there is nothing here for a
        // summary to prioritise — and it used to carry the longest string in the modal
        // ("Track Duplications Only · CleanID No Threshold") on the row that matters least,
        // which crowded the chevron and pulled the eye down the list. Its five values are all
        // in the panel.
        flagged: false, blocked: false, summary: '',
        question: 'Are these right for this study?',
        // The only section that can never flag — NAV can read that de-duplication and CleanID
        // are configured, but has no way to judge whether they suit this study. That made it
        // the one section with no way at all to act on a "no", which is what this fixes. The
        // destination is Settings; the label names the Quality block within it, since the
        // settings page's own left-hand nav is what gets you there.
        linkSection: 'settings',
        noLabel: 'Go to Project Settings > Quality'
      }
    );
    // Vendors is pinned to the top rather than sorted with the rest. It's the section this
    // checklist is fundamentally about, it's the only one whose fixes live on a different page
    // (Links & Vendors, not Settings), and — the reason it came up — a list whose first row
    // moves between visits is disorienting: clearing a completes mismatch made Vendors clean,
    // which dropped it below a flagged Field dates, so the row the user expected to open first
    // had gone somewhere else. The other four still float their problems up.
    const [vendors, ...rest] = all;
    return [vendors, ...rest.sort((a, b) => this.sortRank(a) - this.sortRank(b))];
  }

  private sortRank(s: ChecklistSection): number {
    return s.blocked ? 0 : s.flagged ? 1 : 2;
  }

  // The dates themselves, not the duration. "25 days in field" is derived — it reads as filler
  // because there's nothing to check it against, whereas a start and end date are the two facts
  // the user actually holds an opinion about. Days in field stays in the panel, where it sits
  // next to the dates it's computed from and earns its place as a sanity check.
  get datesSummary(): string {
    if (!this.fieldStartDate || !this.fieldEndDate) return 'Not set';
    if (this.datesInPast) return 'Already ended';
    // Plain hyphen, not an en dash. Nav uses no typographic characters anywhere in main or core
    // — no em dashes, en dashes or curly quotes — so anything fancier reads as foreign here.
    return `${this.formatShortDate(this.fieldStartDate)} - ${this.formatShortDate(this.fieldEndDate)}`;
  }

  get remainingCount(): number {
    return this.sections.filter(s => !this.confirmed[s.key]).length;
  }

  // Counted separately in the footer, because "3 to confirm" on a modal where one of the three
  // can't be confirmed from here sends the user looking for a control that's deliberately dead.
  //
  // Counts the red lines themselves, not the sections holding them. A red bullet is what the
  // user sees, so a section carrying two of them counts as two — otherwise the total disagrees
  // with what's on screen. The footer doesn't name them ("1 to fix", not "1 error to fix"): the
  // count is already red and sits beside the confirm count, so the word was doing no work.
  get errorCount(): number {
    return this.sections.reduce((n, s) => n + this.sectionBlockers(s.key).length, 0);
  }

  get confirmableRemainingCount(): number {
    return this.sections.filter(s => !this.confirmed[s.key] && !s.blocked).length;
  }

  // The length guard matters now that `sections` is a field rather than a getter: it starts empty
  // and stays empty if load() errors, and "no sections remaining" would otherwise read as
  // "everything confirmed" and enable Confirm & Launch on a modal that failed to load.
  get allConfirmed(): boolean {
    return this.sections.length > 0 && this.remainingCount === 0;
  }

  /**
   * One line, one job: say what this is for. It doesn't count or name what's flagged — each
   * flagged section already says that for itself (the amber dot in the bar, the note once it's
   * open), and the footer gives the running total. Saying it a third time up here read as the
   * header raising an alarm that the row right below it then raised again.
   */
  get lede(): string {
    return this.allConfirmed
      // No count in this string. It said "All six confirmed", but a Healthcare project hides
      // the Quotas section and has one fewer — so the one project type that takes a different
      // path through this modal was the one that read a wrong number back to the user.
      ? "Everything's confirmed. You're clear to launch."
      // Purpose, not mechanics. This used to describe the interaction ("Confirm each one. The
      // next opens as you go."), which told the user how to operate the modal but never why it
      // exists — and "what is this for" was the actual question it was leaving unanswered.
      // Naming the three things that read from this setup is what makes the sign-off feel
      // consequential rather than ceremonial.
      : 'Confirm the setup is right before it goes live - pace, quota flags and DIFOT all read from it.';
  }

  cancel() {
    if (this.saving) return;
    this.close.emit();
  }

  // `confirmed` carries a key per possible section, so a Healthcare project — which hides
  // Quotas — would otherwise report `quotas: false` and read back as quotas reviewed and
  // rejected. The record describes the checklist the user was actually given.
  private get confirmedForShownSections(): Record<string, boolean> {
    return this.sections.reduce((acc, section) => {
      acc[section.key] = this.confirmed[section.key];
      return acc;
    }, {} as Record<string, boolean>);
  }

  /**
   * NK-415: the launch endpoint understands "this one vendor" or "all of them" and a subset is
   * neither, so the ticked vendors go through their own call. It is a single call for the whole
   * set: the earlier per-vendor fan-out ran in parallel and every request pushed the full project
   * to Survey Engine with the other vendors' statuses as it had loaded them, so the last push
   * put launched vendors back to Test there while Navigator showed them as Soft Launch.
   *
   * Failure is collected, not thrown. The project has already moved by the time this runs, so
   * vendors that didn't take are a partial launch to be named in the toast, not a launch to be
   * reported as having failed.
   */
  private applyVendorStatuses(selected: ChecklistPartner[]): Observable<any> {
    this.launchFailures = [];
    if (!selected.length) return of(null);

    return this.projectService.UpdateProjectPartnerStatuses(this.projectId, selected.map(p => p.partnerId), this.status).pipe(
      catchError(() => {
        this.launchFailures = selected.map(p => p.name);
        return of(null);
      })
    );
  }

  confirmAndLaunch() {
    if (this.saving || !this.allConfirmed) return;
    this.saving = true;

    // Read once, before anything is written — the table stays interactive during the save, and
    // a tick changed mid-flight must not land the record and the API on different answers.
    const selected = this.selectedPartners;

    const payload = {
      ...this.launchDetails,
      status: this.status,
      partnerId: this.partnerId,
      // NK-415: false, always. applyToMore is the API's "fan out across every vendor" switch
      // (ProjectService.cs:2033) and that fan-out is the behaviour this ticket exists to
      // remove. The project itself still moves — partnerId is null on this modal, and the API
      // updates the project status whenever that is the case.
      applyToMore: false,
      fieldStartDate: this.fieldStartDate,
      fieldEndDate: this.fieldEndDate
    };

    this.projectService.LaunchSurvey(this.projectId, payload).pipe(
      switchMap(() => this.applyVendorStatuses(selected))
    ).subscribe(() => {
      // NK-315: the record has to carry what was on screen, not just that a box was ticked —
      // "quotas confirmed on the 11th" is worth nothing if the quotas changed on the 14th.
      // The vendor snapshot in particular is what makes confirming a "Not tested" vendor an
      // attributable act rather than an untraceable click.
      this.projectService.SaveGoLiveChecklistAttestation(this.projectId, {
        confirmed: this.confirmedForShownSections,
        confirmedAt: this.confirmedAt,
        submittedAt: new Date().toISOString(),
        snapshot: {
          // No liveUrl. The record carries what was on screen, and the live URL no longer is —
          // keeping it would put a value in the attestation that nobody was shown or asked about.
          quotaGroups: this.quotaGroupNames,
          // NK-415: which vendors were actually launched is now a choice the user made, so the
          // record has to carry it. Without it the snapshot reads as five vendors reviewed and
          // stays silent on two being deliberately held, which is the part worth attesting to.
          vendors: this.partners.map(p => ({
            name: p.name, fullQuota: p.fullQuota, softQuota: p.softQuota,
            cpi: p.cpi, testIdsReturned: p.testIdsReturned, lastTestedOn: p.lastTestedOn,
            launched: selected.includes(p), alreadyLaunched: p.alreadyLaunched
          })),
          fieldStartDate: this.fieldStartDate,
          fieldEndDate: this.fieldEndDate,
          completes: this.completesValue,
          incidence: this.projectIR,
          loi: this.projectLOI,
          isDeDupeEnabled: this.project?.isDeDupeDeviceEnabled,
          isDupeTrackingOnly: this.project?.isDupeTrackingOnly,
          dedupeStatuses: this.dedupeStatuses,
          dedupeProjects: this.dedupeProjectNames,
          isFraudDetectionEnabled: this.project?.isFraudDetectionEnabled,
          fraudDetectionThreshold: this.project?.fraudDetectionThreshold,
          allowMobile: this.project?.allowMobile,
          linkSecurity: this.linkSecurityOn,
          totalVendorAllocation: this.totalVendorAllocation
        }
      }).subscribe(
        () => {},
        // Already launched by this point, so this is a missing record, not a failed launch —
        // said out loud rather than swallowed, but not reported as a launch failure.
        () => this.alertify.warning('Project launched, but the Go Live Checklist record could not be saved.')
      );

      // The message names what happened rather than claiming the whole project. "and across all
      // vendors" was true when the launch always fanned out, and became the bug report that
      // opened NK-415 the moment it stopped being true.
      if (this.launchFailures.length) {
        this.alertify.warning('Soft Launch applied to the project, but ' + this.joinPhrases(this.launchFailures) + ' could not be updated.');
      } else {
        this.alertify.success('Soft Launch status applied to Overall Project' + (selected.length ? ' and ' + selected.length + (selected.length === 1 ? ' vendor' : ' vendors') : ''));
      }
      this.saving = false;
      setTimeout(() => {
        this.saved.emit({
          status: this.status,
          partnerId: this.partnerId,
          applyToMore: false,
          // NK-415: the parent repaints its own vendor rows from this rather than re-fetching,
          // so it needs the list. Absent it, the field plan grid paints every vendor as
          // launched (project-card-field-plan.component.ts) until the next refresh.
          launchedPartnerIds: selected.map(p => p.partnerId)
        });
      }, 500);
    }, () => {
      this.saving = false;
      this.alertify.error('There was an issue updating the status');
    });
  }
}
